import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiEye,
  FiFolderPlus,
  FiPlus,
  FiTrash2,
  FiX,
} from 'react-icons/fi';
import {
  MY_PLAYLISTS_CATEGORY_KEY,
  addPlaylistToBrowseCategory,
  builtinCategoryKey,
  clearAllBrowseHides,
  applySyncedBrowsePrefs,
  browsePrefsIsEmpty,
  browsePrefsNeedsLocalMigration,
  buildBrowsePrefsFromLocalStorage,
  clearCloudBrowsePrefsCache,
  catalogStationHideKey,
  customCategoryKey,
  customStationHideKey,
  deleteBrowseCategory,
  isCategoryCollapsed,
  isCategoryHidden,
  isStationHidden,
  listBrowseCategories,
  listBrowseCategoriesLocalOnly,
  removePlaylistFromBrowseCategory,
  saveBrowseCategory,
  setCategoryCollapsed,
  setCategoryHidden,
  setStationHidden,
  readHiddenStationKeys,
} from './audioBrowseCategoryStorage';
import {
  groupGenreStationsByGenreId,
  listGenreStations,
  listGenreStationsLocalOnly,
  saveGenreStation,
} from './audioGenreStationStorage';
import { subscribeAudioLibraryChanged } from './audioLibraryEvents';
import { listPlaylists, listPlaylistsLocalOnly, savePlaylist } from './audioPlaylistStorage';
import {
  cloudSaveCategory,
  cloudSaveBrowsePrefs,
  cloudSaveGenreStation,
  cloudSavePlaylist,
  cloudSaveTileAppearance,
  hasCloudAudioLibrary,
  syncCloudLibrary,
} from './audioPlayerLibraryApi';
import { mirrorCloudLibraryToLocal } from './audioPlayerLocalMirror';
import { findStreamByUrl, WEB_STREAM_GENRES } from './webStreamCatalog';
import SavedPlaylistPickerDropdown from './SavedPlaylistPickerDropdown';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import ConfirmDangerModal from '../ConfirmDangerModal/ConfirmDangerModal';
import AudioLandingTile from './AudioLandingTile';
import {
  applySyncedTileAppearances,
  clearCloudTileAppearanceCache,
  builtinGenreAppearanceKey,
  catalogStreamAppearanceKey,
  getLocalTileAppearance,
  readTileAppearanceMapLocalOnly,
  resolveTileColor,
  sanitizeTileColor,
  saveTileAppearance,
  sectionAppearanceKey,
} from './audioTileAppearance';

function CollapsibleCategoryHeader({
  categoryKey,
  title,
  count,
  collapsed,
  onToggle,
  actions = null,
  accentColorPicker = null,
  beforeCount = null,
  editable = false,
  editing = false,
  editValue = '',
  onEditValueChange,
  onEditCommit,
  onEditCancel,
  onEditStart,
}) {
  const countBadge =
    typeof count === 'number' ? (
      <span className="audio-player-landing__genre-count">{count}</span>
    ) : null;

  if (editable) {
    return (
      <div className="audio-player-landing__genre-head">
        <div
          className="audio-player-landing__genre-toggle audio-player-landing__genre-toggle--editable"
          aria-expanded={!collapsed}
        >
          <button
            type="button"
            className="audio-player-landing__genre-chevron-btn"
            aria-label={collapsed ? 'Expand genre' : 'Collapse genre'}
            onClick={() => onToggle(categoryKey, !collapsed)}
          >
            {collapsed ? (
              <FiChevronRight className="audio-player-landing__genre-chevron" aria-hidden />
            ) : (
              <FiChevronDown className="audio-player-landing__genre-chevron" aria-hidden />
            )}
          </button>
          {accentColorPicker}
          {editing ? (
            <input
              type="text"
              className="audio-player-landing__genre-title-input"
              value={editValue}
              onChange={(e) => onEditValueChange?.(e.target.value)}
              onBlur={() => onEditCommit?.()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onEditCommit?.();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onEditCancel?.();
                }
              }}
              maxLength={80}
              aria-label="Genre name"
              autoFocus
            />
          ) : (
            <span
              className="audio-player-landing__genre-toggle-title audio-player-landing__genre-toggle-title--editable"
              title="Double-click to rename"
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditStart?.();
              }}
            >
              {title}
            </span>
          )}
          {beforeCount}
          {countBadge}
        </div>
        {actions ? <div className="audio-player-landing__genre-head-actions">{actions}</div> : null}
      </div>
    );
  }

  return (
    <div className="audio-player-landing__genre-head">
      <div className="audio-player-landing__genre-toggle" aria-expanded={!collapsed}>
        <button
          type="button"
          className="audio-player-landing__genre-chevron-btn"
          aria-label={collapsed ? 'Expand genre' : 'Collapse genre'}
          onClick={() => onToggle(categoryKey, !collapsed)}
        >
          {collapsed ? (
            <FiChevronRight className="audio-player-landing__genre-chevron" aria-hidden />
          ) : (
            <FiChevronDown className="audio-player-landing__genre-chevron" aria-hidden />
          )}
        </button>
        {accentColorPicker}
        <button
          type="button"
          className="audio-player-landing__genre-title-hit"
          onClick={() => onToggle(categoryKey, !collapsed)}
        >
          <span className="audio-player-landing__genre-toggle-title">{title}</span>
        </button>
        {beforeCount}
        {countBadge}
      </div>
      {actions ? <div className="audio-player-landing__genre-head-actions">{actions}</div> : null}
    </div>
  );
}

function GenreAddStationButton({ label, onClick, active = false }) {
  return (
    <button
      type="button"
      className={`audio-player-landing__genre-inline-add${active ? ' audio-player-landing__genre-inline-add--active' : ''}`}
      aria-label={`Add station to ${label}`}
      title="Add station"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }}
    >
      <FiPlus aria-hidden />
    </button>
  );
}

function GenreSectionHideButton({ categoryKey, label, onHide }) {
  return (
    <button
      type="button"
      className="audio-player-landing__category-hide"
      aria-label={`Hide ${label}`}
      title="Hide this section"
      onClick={() => onHide(categoryKey, label)}
    >
      <FiX aria-hidden />
    </button>
  );
}

function GenreSectionColorPicker({ label, color, genreId, onChange }) {
  return (
    <label
      className="audio-player-landing__genre-color"
      title={`Accent colour for ${label}`}
      onClick={(e) => e.stopPropagation()}
    >
      <span className="audio-player-landing__sr-only">Accent colour for {label}</span>
      <input
        type="color"
        value={resolveTileColor(color, genreId)}
        onChange={(e) => onChange?.(sanitizeTileColor(e.target.value))}
        onClick={(e) => e.stopPropagation()}
      />
    </label>
  );
}

function PlaylistTile({ row, onOpen, onRemove = null, onSaveAppearance, accentFallback = null }) {
  const meta = `${row.tracks?.length || 0} tracks${row.shuffle ? ' · shuffle' : ''}${row.repeat ? ' · repeat' : ''}`;
  return (
    <li>
      <div className="audio-player-landing__tile-wrap">
        <AudioLandingTile
          variant="playlist"
          genreId="playlist"
          defaultTitle={row.name}
          tileLabel={row.tileLabel}
          tileColor={row.tileColor || accentFallback}
          meta={meta}
          badge="Playlist"
          onActivate={() => onOpen(row)}
          onSaveAppearance={onSaveAppearance}
          customizeLabel={`Customize ${row.name} tile`}
        />
        {onRemove ? (
          <button
            type="button"
            className="audio-player-landing__tile-remove"
            aria-label={`Remove ${row.name} from category`}
            onClick={() => onRemove(row.id)}
          >
            <FiX aria-hidden />
          </button>
        ) : null}
      </div>
    </li>
  );
}

function StationTile({
  stream,
  genreId,
  onPlayStream,
  onDismiss = null,
  onSaveAppearance,
  defaultTitle,
  tileLabel = null,
  tileColor = null,
}) {
  const title = defaultTitle || stream.title;
  return (
    <li>
      <div className="audio-player-landing__tile-wrap">
        <AudioLandingTile
          variant="station"
          genreId={genreId}
          defaultTitle={title}
          tileLabel={tileLabel}
          tileColor={tileColor}
          meta={stream.subtitle}
          onActivate={() => onPlayStream?.(stream.url, title, findStreamByUrl(stream.url))}
          onSaveAppearance={onSaveAppearance}
          customizeLabel={`Customize ${title} tile`}
        />
        {onDismiss ? (
          <button
            type="button"
            className="audio-player-landing__tile-remove"
            aria-label={`Hide ${title}`}
            title="Hide station"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDismiss();
            }}
          >
            <FiX aria-hidden />
          </button>
        ) : null}
      </div>
    </li>
  );
}

export default function AudioPlayerLanding({
  onPlayStream,
  onLoadSavedPlaylist,
  onLoadStorageBoxPlaylist,
  authed = false,
}) {
  const [savedPlaylists, setSavedPlaylists] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [collapseTick, setCollapseTick] = useState(0);
  const [loadError, setLoadError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [editingGenreId, setEditingGenreId] = useState('');
  const [editingGenreName, setEditingGenreName] = useState('');
  const [hiddenRevision, setHiddenRevision] = useState(0);
  const [confirmModal, setConfirmModal] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [customGenreStations, setCustomGenreStations] = useState([]);
  const [addStationGenreId, setAddStationGenreId] = useState('');
  const [newStationUrl, setNewStationUrl] = useState('');
  const [newStationTitle, setNewStationTitle] = useState('');
  const [newStationSubtitle, setNewStationSubtitle] = useState('');
  const [addStationBusy, setAddStationBusy] = useState(false);
  const [appearanceRevision, setAppearanceRevision] = useState(0);
  const refreshInFlightRef = useRef(null);

  const bumpAppearance = useCallback(() => setAppearanceRevision((n) => n + 1), []);

  const refreshAll = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const run = (async () => {
      let playlistsError = '';

      const useCloudLibrary = authed && (await hasCloudAudioLibrary());
      if (!useCloudLibrary) {
        clearCloudBrowsePrefsCache();
        clearCloudTileAppearanceCache();
      }

      try {
        if (useCloudLibrary) {
          let library = await syncCloudLibrary();
          const cloudEmpty =
            !(library.playlists?.length || 0) &&
            !(library.categories?.length || 0) &&
            !(library.genreStations?.length || 0);
          if (cloudEmpty) {
            const [localPlaylists, localCategories, localStations] = await Promise.all([
              listPlaylistsLocalOnly(),
              listBrowseCategoriesLocalOnly(),
              listGenreStationsLocalOnly(),
            ]);
            const hasLocal =
              localPlaylists.length > 0 || localCategories.length > 0 || localStations.length > 0;
            if (hasLocal) {
              await Promise.all([
                ...localPlaylists.map((row) =>
                  cloudSavePlaylist({
                    id: row.id,
                    name: row.name,
                    tracks: row.tracks,
                    shuffle: Boolean(row.shuffle),
                    repeat: Boolean(row.repeat),
                    eqGains: row.eqGains ?? null,
                    tileLabel: row.tileLabel ?? null,
                    tileColor: row.tileColor ?? null,
                  })
                ),
                ...localCategories.map((row) =>
                  cloudSaveCategory({
                    id: row.id,
                    name: row.name,
                    playlistIds: row.playlistIds,
                    tileLabel: row.tileLabel ?? null,
                    tileColor: row.tileColor ?? null,
                  })
                ),
                ...localStations.map((row) =>
                  cloudSaveGenreStation({
                    id: row.id,
                    genreId: row.genreId,
                    title: row.title,
                    subtitle: row.subtitle,
                    url: row.url,
                    tileLabel: row.tileLabel ?? null,
                    tileColor: row.tileColor ?? null,
                  })
                ),
              ]);
              library = await syncCloudLibrary();
            }
          }
          if (!(library.tileAppearances?.length || 0)) {
            const localAppearance = readTileAppearanceMapLocalOnly();
            const localKeys = Object.keys(localAppearance);
            if (localKeys.length) {
              await Promise.all(
                localKeys.map((key) =>
                  cloudSaveTileAppearance({
                    key,
                    tileLabel: localAppearance[key]?.tileLabel ?? null,
                    tileColor: localAppearance[key]?.tileColor ?? null,
                  })
                )
              );
              library = await syncCloudLibrary();
            }
          }
          if (browsePrefsNeedsLocalMigration(library.browsePrefs)) {
            const localPrefs = buildBrowsePrefsFromLocalStorage();
            if (!browsePrefsIsEmpty(localPrefs)) {
              await cloudSaveBrowsePrefs(localPrefs);
              library = await syncCloudLibrary();
            }
          }
          applySyncedTileAppearances(library.tileAppearances);
          applySyncedBrowsePrefs(library.browsePrefs);
          setCollapseTick((n) => n + 1);
          setHiddenRevision((n) => n + 1);
          await mirrorCloudLibraryToLocal(library);
          setSavedPlaylists(Array.isArray(library.playlists) ? library.playlists : []);
          setCustomCategories(Array.isArray(library.categories) ? library.categories : []);
          setCustomGenreStations(Array.isArray(library.genreStations) ? library.genreStations : []);
          setLoadError('');
          return;
        }
      } catch (err) {
        console.warn('[audio-player] Cloud library sync failed', err);
        clearCloudBrowsePrefsCache();
        clearCloudTileAppearanceCache();
        playlistsError =
          'Could not load your saved library from the account. Showing this device copy if available.';
      }

      let playlists = null;
      let categories = null;
      let genreStations = null;

      try {
        playlists = await listPlaylists();
      } catch (err) {
        console.warn('[audio-player] Could not load saved playlists', err);
        if (!playlistsError) {
          playlistsError =
            'Saved playlists could not be loaded. Allow storage for this site, close other tabs, and refresh once.';
        }
      }

      try {
        categories = await listBrowseCategories();
      } catch (err) {
        console.warn('[audio-player] Could not load custom genres', err);
      }

      try {
        genreStations = await listGenreStations();
      } catch (err) {
        console.warn('[audio-player] Could not load custom genre stations', err);
      }

      if (playlists !== null) {
        setSavedPlaylists(Array.isArray(playlists) ? playlists : []);
      }
      if (categories !== null) {
        setCustomCategories(Array.isArray(categories) ? categories : []);
      }
      if (genreStations !== null) {
        setCustomGenreStations(Array.isArray(genreStations) ? genreStations : []);
      }

      setLoadError(playlistsError);
    })();

    refreshInFlightRef.current = run;
    try {
      await run;
    } finally {
      if (refreshInFlightRef.current === run) {
        refreshInFlightRef.current = null;
      }
    }
  }, [authed]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    const onSession = () => refreshAll();
    window.addEventListener('bahlus-session-validated', onSession);
    return () => window.removeEventListener('bahlus-session-validated', onSession);
  }, [refreshAll]);

  useEffect(() => {
    return subscribeAudioLibraryChanged(() => {
      refreshAll();
    });
  }, [refreshAll]);

  const collapsed = useCallback(
    (key) => {
      void collapseTick;
      return isCategoryCollapsed(key, false);
    },
    [collapseTick]
  );

  const toggleCollapse = useCallback((key, nextCollapsed) => {
    setCategoryCollapsed(key, nextCollapsed);
    setCollapseTick((n) => n + 1);
  }, []);

  const hiddenCategoryKeys = useMemo(() => {
    void hiddenRevision;
    return new Set(
      [
        MY_PLAYLISTS_CATEGORY_KEY,
        ...customCategories.map((c) => customCategoryKey(c.id)),
        ...WEB_STREAM_GENRES.map((g) => builtinCategoryKey(g.id)),
      ].filter((key) => isCategoryHidden(key))
    );
  }, [customCategories, hiddenRevision]);

  const closeConfirmModal = useCallback(() => {
    if (confirmBusy) return;
    setConfirmModal(null);
  }, [confirmBusy]);

  const handleConfirmModal = useCallback(async () => {
    if (!confirmModal?.onConfirm || confirmBusy) return;
    setConfirmBusy(true);
    try {
      await confirmModal.onConfirm();
      setConfirmModal(null);
    } finally {
      setConfirmBusy(false);
    }
  }, [confirmModal, confirmBusy]);

  const hideCategory = useCallback((categoryKey, label) => {
    setConfirmModal({
      variant: 'confirm',
      title: 'Hide this section?',
      subtitle: label,
      description: 'You can show it again with “Show hidden” next to Browse stations.',
      confirmLabel: 'Hide',
      onConfirm: () => {
        setCategoryHidden(categoryKey, true);
        setCategoryCollapsed(categoryKey, true);
        setHiddenRevision((n) => n + 1);
        setStatusMessage(`“${label}” hidden. Use “Show hidden” in Browse stations to restore.`);
      },
    });
  }, []);

  const restoreHiddenCategories = useCallback(() => {
    clearAllBrowseHides();
    setHiddenRevision((n) => n + 1);
    setStatusMessage('All hidden sections and stations are visible again.');
  }, []);

  const hideStation = useCallback((stationKey, title) => {
    setConfirmModal({
      variant: 'confirm',
      title: 'Hide this station?',
      subtitle: title,
      description: 'You can show it again with “Show hidden” next to Browse stations.',
      confirmLabel: 'Hide',
      onConfirm: () => {
        setStationHidden(stationKey, true);
        setHiddenRevision((n) => n + 1);
        setStatusMessage(`“${title}” hidden. Use “Show hidden” in Browse stations to restore.`);
      },
    });
  }, []);

  const hiddenSectionCount = hiddenCategoryKeys.size;

  const hiddenStationCount = useMemo(() => {
    void hiddenRevision;
    return readHiddenStationKeys().length;
  }, [hiddenRevision]);

  const playlistById = useMemo(() => {
    const map = new Map();
    savedPlaylists.forEach((p) => map.set(p.id, p));
    return map;
  }, [savedPlaylists]);

  const customStationsByGenre = useMemo(
    () => groupGenreStationsByGenreId(customGenreStations),
    [customGenreStations]
  );

  const resetAddStationForm = () => {
    setAddStationGenreId('');
    setNewStationUrl('');
    setNewStationTitle('');
    setNewStationSubtitle('');
  };

  const openAddStationForm = (genreId) => {
    const key = builtinCategoryKey(genreId);
    setCategoryCollapsed(key, false);
    setCollapseTick((n) => n + 1);
    setAddStationGenreId(genreId);
    setNewStationUrl('');
    setNewStationTitle('');
    setNewStationSubtitle('');
  };

  const handleAddStationBlur = () => {
    const trimmed = newStationUrl.trim();
    if (!trimmed || newStationTitle.trim()) return;
    const meta = findStreamByUrl(trimmed);
    if (meta?.title) setNewStationTitle(meta.title);
  };

  const handleSaveGenreStation = async (e, genreId) => {
    e.preventDefault();
    setAddStationBusy(true);
    try {
      await saveGenreStation({
        genreId,
        url: newStationUrl,
        title: newStationTitle,
        subtitle: newStationSubtitle,
      });
      setStatusMessage('Station added to genre.');
      resetAddStationForm();
      await refreshAll();
    } catch (err) {
      setStatusMessage(err.message || 'Could not add station.');
    } finally {
      setAddStationBusy(false);
    }
  };

  const handlePlaylistOpen = (row) => {
    const urlTracks = (row.tracks || []).filter((t) => t.source === 'url' && t.url);
    const storageTracks = (row.tracks || []).filter((t) => t.source === 'storagebox' && t.itemId);
    if (urlTracks.length) {
      setLoadError('');
      onLoadSavedPlaylist?.(row, urlTracks);
      return;
    }
    if (storageTracks.length) {
      if (authed && onLoadStorageBoxPlaylist) {
        setLoadError('');
        onLoadStorageBoxPlaylist(row, storageTracks);
        return;
      }
      setLoadError(
        authed
          ? 'This Storage Box playlist could not be opened.'
          : 'This playlist uses Storage Box files — sign in to play them from your drive.'
      );
      return;
    }
    setLoadError('This playlist has no playable tracks.');
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      const created = await saveBrowseCategory({ name, playlistIds: [] });
      setNewCategoryName('');
      setShowNewCategoryForm(false);
      setStatusMessage(`Genre “${name}” created. Expand it and add playlists below.`);
      await refreshAll();
      if (created?.id) {
        setCategoryCollapsed(customCategoryKey(created.id), false);
        setCollapseTick((n) => n + 1);
      }
    } catch (err) {
      setStatusMessage(err.message || 'Could not create genre.');
    }
  };

  const requestDeleteCategory = (categoryId, name) => {
    setConfirmModal({
      variant: 'danger',
      title: 'Delete this genre?',
      subtitle: name,
      description: 'Saved playlists are not deleted — only this genre folder is removed on this device.',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await deleteBrowseCategory(categoryId);
          setStatusMessage(`Removed genre “${name}”.`);
          await refreshAll();
        } catch (err) {
          setStatusMessage(err.message || 'Could not delete genre.');
        }
      },
    });
  };

  const requestRemovePlaylistFromCategory = (categoryId, playlistId, playlistName) => {
    setConfirmModal({
      variant: 'confirm',
      title: 'Remove from genre?',
      subtitle: playlistName || 'Playlist',
      description: 'The playlist stays in My playlists; it is only unlinked from this genre.',
      confirmLabel: 'Remove',
      onConfirm: async () => {
        try {
          await removePlaylistFromBrowseCategory(categoryId, playlistId);
          await refreshAll();
        } catch (err) {
          setStatusMessage(err.message || 'Could not remove playlist.');
        }
      },
    });
  };

  const handleAddPlaylistToCategory = async (categoryId, playlistId) => {
    if (!categoryId || !playlistId) return;
    try {
      await addPlaylistToBrowseCategory(categoryId, playlistId);
      setStatusMessage('Playlist added to genre.');
      await refreshAll();
    } catch (err) {
      setStatusMessage(err.message || 'Could not add playlist.');
    }
  };

  const startEditGenre = (cat) => {
    setEditingGenreId(cat.id);
    setEditingGenreName(cat.name || '');
  };

  const cancelEditGenre = () => {
    setEditingGenreId('');
    setEditingGenreName('');
  };

  const commitEditGenre = async (cat) => {
    const name = editingGenreName.trim();
    if (!editingGenreId || editingGenreId !== cat.id) return;
    if (!name) {
      setStatusMessage('Genre name cannot be empty.');
      cancelEditGenre();
      return;
    }
    if (name === cat.name) {
      cancelEditGenre();
      return;
    }
    try {
      await saveBrowseCategory({
        id: cat.id,
        name,
        playlistIds: cat.playlistIds || [],
        tileLabel: cat.tileLabel,
        tileColor: cat.tileColor,
      });
      setStatusMessage(`Genre renamed to “${name}”.`);
      cancelEditGenre();
      await refreshAll();
    } catch (err) {
      setStatusMessage(err.message || 'Could not rename genre.');
    }
  };

  const myPlaylistsAccent = getLocalTileAppearance(sectionAppearanceKey(MY_PLAYLISTS_CATEGORY_KEY)).tileColor;

  const handleMySectionColor = async (color) => {
    try {
      await saveTileAppearance(sectionAppearanceKey(MY_PLAYLISTS_CATEGORY_KEY), { tileColor: color });
      bumpAppearance();
    } catch (e) {
      setStatusMessage(e.message || 'Could not save colour.');
    }
  };

  const handleBuiltinGenreColor = async (genreId, color) => {
    try {
      await saveTileAppearance(builtinGenreAppearanceKey(genreId), { tileColor: color });
      bumpAppearance();
    } catch (e) {
      setStatusMessage(e.message || 'Could not save colour.');
    }
  };

  const handleSavePlaylistTile = async (row, appearance) => {
    await savePlaylist({
      id: row.id,
      name: row.name,
      tracks: row.tracks,
      shuffle: row.shuffle,
      repeat: row.repeat,
      eqGains: row.eqGains,
      tileLabel: appearance?.tileLabel,
      tileColor: appearance?.tileColor,
    });
    await refreshAll();
    setStatusMessage('Playlist tile updated.');
  };

  const handleSaveCategoryTile = async (cat, appearance) => {
    await saveBrowseCategory({
      id: cat.id,
      name: cat.name,
      playlistIds: cat.playlistIds || [],
      tileLabel: appearance?.tileLabel,
      tileColor: appearance?.tileColor,
    });
    await refreshAll();
    setStatusMessage('Genre tile settings updated.');
  };

  const handleSaveCustomStationTile = async (stream, appearance) => {
    await saveGenreStation({
      id: stream.id,
      genreId: stream.genreId,
      title: stream.title,
      subtitle: stream.subtitle,
      url: stream.url,
      tileLabel: appearance?.tileLabel,
      tileColor: appearance?.tileColor,
    });
    await refreshAll();
    setStatusMessage('Station tile updated.');
  };

  const handleSaveCatalogStationTile = async (genreId, stream, appearance) => {
    try {
      await saveTileAppearance(catalogStreamAppearanceKey(genreId, stream.id), appearance);
      bumpAppearance();
      setStatusMessage('Station tile updated.');
    } catch (e) {
      setStatusMessage(e.message || 'Could not save station tile.');
    }
  };

  const resolveCatalogStationAppearance = useCallback(
    (genreId, stream) => {
      const row = getLocalTileAppearance(catalogStreamAppearanceKey(genreId, stream.id));
      const genreTint = getLocalTileAppearance(builtinGenreAppearanceKey(genreId)).tileColor;
      return {
        tileLabel: row.tileLabel || null,
        tileColor: row.tileColor || genreTint || null,
      };
    },
    [appearanceRevision]
  );

  return (
    <div className="audio-player-landing">
      <section className="audio-player-landing__section" aria-labelledby="apl-radio-heading">
        <div className="audio-player-landing__section-head">
          <div className="audio-player-landing__section-head-text">
            <div className="audio-player-landing__section-title-row">
              <h2 id="apl-radio-heading">Browse stations</h2>
              {showNewCategoryForm ? (
                <form
                  className="audio-player-landing__category-form audio-player-landing__category-form--inline-title"
                  onSubmit={handleCreateCategory}
                >
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Genre name"
                    maxLength={80}
                    aria-label="Genre name"
                    autoFocus
                  />
                  <button type="submit" className="audio-player-landing__section-icon-btn" aria-label="Create genre">
                    <FiCheck aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="audio-player-landing__section-icon-btn"
                    aria-label="Cancel"
                    onClick={() => {
                      setShowNewCategoryForm(false);
                      setNewCategoryName('');
                    }}
                  >
                    <FiX aria-hidden />
                  </button>
                </form>
              ) : null}
              <div className="audio-player-landing__section-title-actions">
                {(hiddenSectionCount > 0 || hiddenStationCount > 0) ? (
                  <button
                    type="button"
                    className="audio-player-landing__section-icon-btn"
                    aria-label={`Show hidden (${hiddenSectionCount + hiddenStationCount})`}
                    title="Show hidden"
                    onClick={restoreHiddenCategories}
                  >
                    <FiEye aria-hidden />
                  </button>
                ) : null}
                {!showNewCategoryForm ? (
                  <button
                    type="button"
                    className="audio-player-landing__section-icon-btn audio-player-landing__section-icon-btn--accent"
                    aria-label="Add genre"
                    title="Add genre"
                    onClick={() => setShowNewCategoryForm(true)}
                  >
                    <FiFolderPlus aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>
            <p>
              Saved playlists and custom genres are under <strong>Your library</strong> at the top.
              Free radio streams are listed below.
            </p>
          </div>
        </div>

        <h3 id="apl-library-heading" className="audio-player-landing__library-heading">
          Your library
        </h3>

        {!hiddenCategoryKeys.has(MY_PLAYLISTS_CATEGORY_KEY) ? (
          <div className="audio-player-landing__genre-block audio-player-landing__genre-block--library">
            <CollapsibleCategoryHeader
              categoryKey={MY_PLAYLISTS_CATEGORY_KEY}
              title="My playlists"
              count={savedPlaylists.length}
              collapsed={collapsed(MY_PLAYLISTS_CATEGORY_KEY)}
              onToggle={toggleCollapse}
              accentColorPicker={
                <GenreSectionColorPicker
                  label="My playlists"
                  genreId="playlist"
                  color={myPlaylistsAccent}
                  onChange={handleMySectionColor}
                />
              }
              actions={
                <GenreSectionHideButton
                  categoryKey={MY_PLAYLISTS_CATEGORY_KEY}
                  label="My playlists"
                  onHide={hideCategory}
                />
              }
            />
            {!collapsed(MY_PLAYLISTS_CATEGORY_KEY) ? (
              <div className="audio-player-landing__genre-body">
                {savedPlaylists.length > 0 ? (
                  <ul className="audio-player-landing__tile-grid">
                    {savedPlaylists.map((row) => (
                      <PlaylistTile
                        key={row.id}
                        row={row}
                        onOpen={handlePlaylistOpen}
                        accentFallback={myPlaylistsAccent}
                        onSaveAppearance={(appearance) => handleSavePlaylistTile(row, appearance)}
                      />
                    ))}
                  </ul>
                ) : (
                  <p className="audio-player-landing__genre-hint">
                    {authed
                      ? 'No saved playlists in your account yet. Save from the Playlist panel while playing — lists sync across your signed-in devices.'
                      : 'No saved playlists on this device yet. Sign in to sync playlists across devices, or save locally from the Playlist panel.'}
                  </p>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {customCategories
          .filter((cat) => !hiddenCategoryKeys.has(customCategoryKey(cat.id)))
          .map((cat) => {
          const key = customCategoryKey(cat.id);
          const isCollapsed = collapsed(key);
          const playlistsInCat = (cat.playlistIds || [])
            .map((pid) => playlistById.get(pid))
            .filter(Boolean);
          const availableToAdd = savedPlaylists.filter((p) => !(cat.playlistIds || []).includes(p.id));

          return (
            <div key={cat.id} className="audio-player-landing__genre-block audio-player-landing__genre-block--custom">
              <CollapsibleCategoryHeader
                categoryKey={key}
                title={cat.name}
                count={playlistsInCat.length}
                collapsed={isCollapsed}
                onToggle={toggleCollapse}
                editable
                editing={editingGenreId === cat.id}
                editValue={editingGenreId === cat.id ? editingGenreName : cat.name}
                onEditValueChange={setEditingGenreName}
                onEditStart={() => startEditGenre(cat)}
                onEditCommit={() => commitEditGenre(cat)}
                onEditCancel={cancelEditGenre}
                accentColorPicker={
                  <GenreSectionColorPicker
                    label={cat.name}
                    genreId="playlist"
                    color={cat.tileColor}
                    onChange={(color) =>
                      handleSaveCategoryTile(cat, { tileLabel: cat.tileLabel, tileColor: color })
                    }
                  />
                }
                actions={
                  <>
                    <GenreSectionHideButton categoryKey={key} label={cat.name} onHide={hideCategory} />
                    <button
                      type="button"
                      className="audio-player-landing__category-delete"
                      aria-label={`Delete genre ${cat.name}`}
                      onClick={() => requestDeleteCategory(cat.id, cat.name)}
                    >
                      <FiTrash2 aria-hidden />
                    </button>
                  </>
                }
              />
              {!isCollapsed ? (
                <div className="audio-player-landing__genre-body">
                  <div className="audio-player-landing__genre-station-toolbar">
                    {savedPlaylists.length > 0 ? (
                      <label className="audio-player-landing__add-playlist">
                        <span>Add playlist</span>
                        <SavedPlaylistPickerDropdown
                          playlists={availableToAdd}
                          onPick={(playlistId) => handleAddPlaylistToCategory(cat.id, playlistId)}
                          placeholder="Choose saved playlist…"
                          emptyHint="All saved playlists are in this genre"
                        />
                      </label>
                    ) : (
                      <p className="audio-player-landing__genre-hint">
                        Save a playlist from the player panel while listening, then add it here.
                      </p>
                    )}
                  </div>
                  {playlistsInCat.length > 0 ? (
                    <ul className="audio-player-landing__tile-grid">
                      {playlistsInCat.map((row) => (
                        <PlaylistTile
                          key={row.id}
                          row={row}
                          onOpen={handlePlaylistOpen}
                          accentFallback={cat.tileColor}
                          onSaveAppearance={(appearance) => handleSavePlaylistTile(row, appearance)}
                          onRemove={(playlistId) =>
                            requestRemovePlaylistFromCategory(cat.id, playlistId, row.name)
                          }
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className="audio-player-landing__genre-hint">No playlists in this genre yet.</p>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}

        <h3 id="apl-radio-genres-heading" className="audio-player-landing__library-heading">
          Internet radio
        </h3>

        {WEB_STREAM_GENRES.filter((genre) => !hiddenCategoryKeys.has(builtinCategoryKey(genre.id))).map(
          (genre) => {
          const key = builtinCategoryKey(genre.id);
          const isCollapsed = collapsed(key);
          const catalogStreams = genre.streams.filter(
            (stream) => !isStationHidden(catalogStationHideKey(genre.id, stream.id))
          );
          const extraStations = (customStationsByGenre.get(genre.id) || []).filter(
            (stream) => !isStationHidden(customStationHideKey(stream.id))
          );
          const stationCount = catalogStreams.length + extraStations.length;
          const showingAddForm = addStationGenreId === genre.id;

          return (
            <div
              key={genre.id}
              className="audio-player-landing__genre-block audio-player-landing__genre-block--builtin"
              data-genre={genre.id}
            >
              <CollapsibleCategoryHeader
                categoryKey={key}
                title={genre.label}
                count={stationCount}
                collapsed={isCollapsed}
                onToggle={toggleCollapse}
                accentColorPicker={
                  <GenreSectionColorPicker
                    label={genre.label}
                    genreId={genre.id}
                    color={getLocalTileAppearance(builtinGenreAppearanceKey(genre.id)).tileColor}
                    onChange={(color) => handleBuiltinGenreColor(genre.id, color)}
                  />
                }
                actions={
                  <GenreSectionHideButton categoryKey={key} label={genre.label} onHide={hideCategory} />
                }
                beforeCount={
                  <GenreAddStationButton
                    label={genre.label}
                    active={showingAddForm}
                    onClick={() =>
                      showingAddForm ? resetAddStationForm() : openAddStationForm(genre.id)
                    }
                  />
                }
              />
              {!isCollapsed ? (
                <div className="audio-player-landing__genre-body">
                  {showingAddForm ? (
                    <form
                      className="audio-player-landing__add-station-form"
                      onSubmit={(e) => handleSaveGenreStation(e, genre.id)}
                    >
                      <input
                        type="url"
                        value={newStationUrl}
                        onChange={(e) => setNewStationUrl(e.target.value)}
                        onBlur={handleAddStationBlur}
                        placeholder="Stream URL (https://…)"
                        required
                        aria-label="Stream URL"
                      />
                      <input
                        type="text"
                        value={newStationTitle}
                        onChange={(e) => setNewStationTitle(e.target.value)}
                        placeholder="Station name"
                        aria-label="Station name"
                      />
                      <input
                        type="text"
                        value={newStationSubtitle}
                        onChange={(e) => setNewStationSubtitle(e.target.value)}
                        placeholder="Subtitle (optional)"
                        aria-label="Station subtitle"
                      />
                      <button type="submit" disabled={addStationBusy}>
                        {addStationBusy ? 'Saving…' : 'Save station'}
                      </button>
                      <button
                        type="button"
                        className="audio-player-landing__category-form-cancel"
                        onClick={resetAddStationForm}
                      >
                        Cancel
                      </button>
                    </form>
                  ) : null}
                  <ul className="audio-player-landing__tile-grid">
                    {catalogStreams.map((stream) => {
                      const tileApp = resolveCatalogStationAppearance(genre.id, stream);
                      return (
                        <StationTile
                          key={stream.id}
                          stream={stream}
                          genreId={genre.id}
                          tileLabel={tileApp.tileLabel}
                          tileColor={tileApp.tileColor}
                          onPlayStream={onPlayStream}
                          onSaveAppearance={(appearance) =>
                            handleSaveCatalogStationTile(genre.id, stream, appearance)
                          }
                          onDismiss={() =>
                            hideStation(catalogStationHideKey(genre.id, stream.id), stream.title)
                          }
                        />
                      );
                    })}
                    {extraStations.map((stream) => {
                      const genreTint = getLocalTileAppearance(builtinGenreAppearanceKey(genre.id)).tileColor;
                      return (
                        <StationTile
                          key={stream.id}
                          stream={stream}
                          genreId={genre.id}
                          tileLabel={stream.tileLabel}
                          tileColor={stream.tileColor || genreTint}
                          onPlayStream={onPlayStream}
                          onSaveAppearance={(appearance) => handleSaveCustomStationTile(stream, appearance)}
                          onDismiss={() =>
                            hideStation(customStationHideKey(stream.id), stream.title)
                          }
                        />
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
          );
        })}

        {(hiddenSectionCount > 0 || hiddenStationCount > 0) ? (
          <p className="audio-player-landing__restore-hidden">
            <button type="button" className="audio-player-landing__restore-hidden-btn" onClick={restoreHiddenCategories}>
              Show hidden
              {hiddenSectionCount > 0 ? ` sections (${hiddenSectionCount})` : ''}
              {hiddenSectionCount > 0 && hiddenStationCount > 0 ? ' ·' : ''}
              {hiddenStationCount > 0 ? ` stations (${hiddenStationCount})` : ''}
            </button>
          </p>
        ) : null}
      </section>

      {statusMessage ? (
        <p className="audio-player-page__notice" role="status">
          {statusMessage}
        </p>
      ) : null}

      {loadError ? (
        <p className="audio-player-page__notice audio-player-page__notice--error" role="alert">
          {loadError}
        </p>
      ) : null}

      <ConfirmModal
        isOpen={confirmModal?.variant === 'confirm'}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmModal}
        busy={confirmBusy}
        title={confirmModal?.title ?? ''}
        subtitle={confirmModal?.subtitle ?? ''}
        description={confirmModal?.description ?? ''}
        confirmLabel={confirmModal?.confirmLabel ?? 'Confirm'}
        cancelLabel="Cancel"
      />
      <ConfirmDangerModal
        isOpen={confirmModal?.variant === 'danger'}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmModal}
        busy={confirmBusy}
        title={confirmModal?.title ?? ''}
        subtitle={confirmModal?.subtitle ?? ''}
        description={confirmModal?.description ?? ''}
        confirmLabel={confirmModal?.confirmLabel ?? 'Delete'}
        cancelLabel="Cancel"
      />
    </div>
  );
}
